import { z } from "zod";
import { optionalFolderIdSchema } from "./folders.ts";
import { documentIdSchema, storedTrustSchema } from "./knowledge.ts";
import { userIdSchema } from "./tenancy.ts";
import {
  publishItemOptionsSchema,
  publishPreviewSchema,
  wikiMarkdownSchema,
  wikiPageTypeSchema,
  wikiReviewItemSchema,
  wikiTitleSchema,
} from "./wiki.ts";
import { wikiItemWarningSchema } from "./wiki-checks.ts";
import { wikiAgentRevisionSchema, wikiResolveEligibilitySchema } from "./wiki-resolve.ts";
import { wikiOpenThreadSchema, wikiTrustLevelSchema } from "./wiki-threads.ts";

/** Optional cursor header keeps the activity JSON array compatible with the N-1 SPA. */
export const WIKI_ACTIVITY_CURSOR_HEADER = "x-esi-wiki-before";

export const WIKI_CHANGE_SET_ITEMS_MAX = 200;
export const WIKI_OPEN_CHANGE_SETS_MAX = 50;
export const WIKI_CHANGE_SET_TEXT_MAX = 2000;
/**
 * Proposed Markdown one change set may hold in total, over its create and edit items: 4 MB as
 * 4 × 1024 × 1024 bytes of UTF-8 (wiki-collaboration D6).
 */
export const WIKI_CHANGE_SET_BYTES_MAX = 4 * 1024 * 1024;
/** Folder items one change set may hold (wiki-collaboration D6). */
export const WIKI_FOLDER_ITEMS_MAX = 100;
export const wikiTrustReasonSchema = z.enum([
  "requester",
  "peer",
  "manager",
  "own_edits",
  "waiting",
]);
export const wikiChangeSetStatusSchema = z.enum([
  "open",
  "changes_requested",
  "published",
  "discarded",
]);
export const wikiChangeItemStatusSchema = z.enum([
  "pending",
  "approved",
  "rejected",
  "stale",
  "published",
]);
export const wikiChangeOriginSchema = z.enum([
  "person",
  "agent",
  "answer",
  "mcp",
  "ingestion",
  "revert",
]);
export const wikiActorKindSchema = z.enum(["user", "agent", "mcp_client", "job"]);
export const wikiChangeOpSchema = z.enum([
  "create",
  "edit",
  "rename",
  "move",
  "delete",
  // Folder items (wiki-collaboration D5): proposed by the agent, applied at publication.
  "folder_create",
  "folder_rename",
  "folder_move",
  "folder_delete",
]);
export type WikiChangeOp = z.infer<typeof wikiChangeOpSchema>;
/** The folder operations of a change set: structure only, no page and no label. */
export const WIKI_FOLDER_OPS = [
  "folder_create",
  "folder_rename",
  "folder_move",
  "folder_delete",
] as const satisfies readonly WikiChangeOp[];
export function isFolderOp(op: WikiChangeOp): op is (typeof WIKI_FOLDER_OPS)[number] {
  return (WIKI_FOLDER_OPS as readonly string[]).includes(op);
}
/** Why a submitted item's contradiction check did not run (wiki-collaboration D7). */
export const wikiCheckSkipReasonSchema = z.enum(["spend_limit", "check_failed", "timed_out"]);
export type WikiCheckSkipReason = z.infer<typeof wikiCheckSkipReasonSchema>;
export const wikiStaleReasonSchema = z.enum(["overlap", "too_large", "deleted", "missing"]);
const version = z.int().min(1);
const page = { pageId: documentIdSchema, baseVersion: version };
export const createWikiChangeItemRequestSchema = z.discriminatedUnion("op", [
  z.object({
    op: z.literal("create"),
    title: wikiTitleSchema,
    pageType: wikiPageTypeSchema.optional(),
    markdown: wikiMarkdownSchema,
    folderId: optionalFolderIdSchema,
  }),
  z.object({
    op: z.literal("edit"),
    ...page,
    title: wikiTitleSchema,
    pageType: wikiPageTypeSchema.optional(),
    markdown: wikiMarkdownSchema,
  }),
  z.object({ op: z.literal("rename"), ...page, title: wikiTitleSchema }),
  z.object({ op: z.literal("move"), pageId: documentIdSchema, folderId: optionalFolderIdSchema }),
  z.object({ op: z.literal("delete"), ...page }),
]);
export type CreateWikiChangeItemRequest = z.infer<typeof createWikiChangeItemRequestSchema>;
export const createWikiChangeSetRequestSchema = z.object({
  title: wikiTitleSchema,
  rationale: z.string().max(WIKI_CHANGE_SET_TEXT_MAX).default(""),
  items: z.array(createWikiChangeItemRequestSchema).min(1).max(WIKI_CHANGE_SET_ITEMS_MAX),
});
export type CreateWikiChangeSetRequest = z.infer<typeof createWikiChangeSetRequestSchema>;
export const updateWikiChangeItemRequestSchema = z.object({
  title: wikiTitleSchema.optional(),
  markdown: wikiMarkdownSchema.optional(),
  pageType: wikiPageTypeSchema.optional(),
  folderId: optionalFolderIdSchema,
  baseVersion: version.optional(),
});
export type UpdateWikiChangeItemRequest = z.infer<typeof updateWikiChangeItemRequestSchema>;
export const sendBackWikiChangeSetRequestSchema = z.object({
  comment: z.string().trim().min(1).max(WIKI_CHANGE_SET_TEXT_MAX),
});
export const approveWikiChangeItemRequestSchema = z.object({});
/**
 * "Approve all" (wiki-collaboration, user decision 2026-10-30): items under an open change
 * request stay pending and are counted here. An N-1 API answers `204` with no body.
 */
export const approveAllWikiChangeSetResponseSchema = z.object({
  skippedChangeRequested: z.int().min(0),
  /** At `peer`: items the caller last edited, left for someone else to approve. */
  skippedOwnEdits: z.int().min(0).optional(),
  /**
   * While `wiki.mcpSelfPublish` is off: items of a change set the caller's own MCP client
   * proposed, left for someone else to approve (#328). Absent from an N-1 API.
   */
  skippedSelfPublishMcp: z.int().min(0).optional(),
});
export type ApproveAllWikiChangeSetResponse = z.infer<typeof approveAllWikiChangeSetResponseSchema>;
export const publishWikiChangeSetRequestSchema = z.object({
  includePending: z.boolean().optional(),
  items: z
    .array(publishItemOptionsSchema.extend({ id: z.uuid() }))
    .max(WIKI_CHANGE_SET_ITEMS_MAX)
    .optional(),
});
export type PublishWikiChangeSetRequest = z.infer<typeof publishWikiChangeSetRequestSchema>;
export const wikiChangeItemSchema = z.object({
  id: z.uuid(),
  changeSetId: z.uuid(),
  op: wikiChangeOpSchema,
  position: z.int().min(0),
  pageId: documentIdSchema.nullable(),
  baseVersion: version.nullable(),
  rebasedFromVersion: version.nullable(),
  title: z.string(),
  pageType: wikiPageTypeSchema,
  markdown: z.string().nullable(),
  folderId: z.uuid().nullable(),
  trust: storedTrustSchema,
  status: wikiChangeItemStatusSchema,
  approvedByName: z.string().nullable().optional(),
  approvedBy: userIdSchema.nullable(),
  approvedAt: z.string().nullable(),
  rejectedByName: z.string().nullable().optional(),
  rejectedBy: userIdSchema.nullable(),
  rejectedAt: z.string().nullable(),
  staleReason: wikiStaleReasonSchema.nullable(),
  contradictionCheck: z.enum(["checked", "not_checked"]).nullable(),
  /** Why the submission check left it "not checked"; absent from an N-1 API. */
  contradictionCheckReason: wikiCheckSkipReasonSchema.nullable().optional(),
  /** A folder item's folder, or a page item's folder named by path (folder names, root first). */
  folderPath: z.array(z.string()).nullable().optional(),
  /** `folder_rename`: the new name. */
  folderNewName: z.string().nullable().optional(),
  /** `folder_move`: the target parent's path (empty: the section root). */
  folderTargetPath: z.array(z.string()).nullable().optional(),
  publishedVersion: version.nullable(),
  publishedDocumentId: documentIdSchema.nullable(),
  createdBy: userIdSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
  sources: z.array(
    z.object({ id: documentIdSchema, title: z.string().nullable(), deleted: z.boolean() }),
  ),
  baseMarkdown: z.string().nullable().optional(),
  /** OKF warnings for this viewer (create, edit and rename items); never blocking. */
  warnings: z.array(wikiItemWarningSchema).optional(),
  /**
   * An edit's size against its base version, in lines (wiki-collaboration-ux D8): one set's read
   * only; absent from lists, from an N-1 API and when the diff is too large to compute.
   */
  stats: z.object({ added: z.int().min(0), removed: z.int().min(0) }).optional(),
});
export type WikiChangeItem = z.infer<typeof wikiChangeItemSchema>;
export const wikiChangeSetSchema = z.object({
  id: z.uuid(),
  title: z.string(),
  rationale: z.string(),
  status: wikiChangeSetStatusSchema,
  origin: wikiChangeOriginSchema,
  actorKind: wikiActorKindSchema,
  createdBy: userIdSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
  publishedByName: z.string().nullable().optional(),
  publishedBy: userIdSchema.nullable(),
  publishedAt: z.string().nullable(),
  /**
   * When it was submitted for review; null while a conversation's agent still works on it (a
   * *working* change set, wiki-collaboration D1). Absent from an N-1 API: submitted.
   */
  submittedAt: z.string().nullable().optional(),
  /** Who discarded it and when (wiki-collaboration-ux, the read-only view); absent from N-1. */
  discardedBy: userIdSchema.nullable().optional(),
  discardedByName: z.string().nullable().optional(),
  discardedAt: z.string().nullable().optional(),
  /** Whether the viewer may publish it (`wiki.publish`); absent from an N-1 API. */
  mayPublish: z.boolean().optional(),
  /**
   * Set for a change set proposed through an MCP client (#152): `others` when its creator may
   * not publish it (`wiki.mcpSelfPublish` off), `anyone` when they may. Absent from an N-1 API.
   */
  mcpPublisher: z.enum(["anyone", "others"]).optional(),
  /**
   * The project's `wiki.trust` level and whether it lets the viewer approve and publish this
   * set (wiki-collaboration D9): one set's read and the review queue (-ux D4); absent from
   * other lists and an N-1 API.
   */
  trust: z
    .object({
      level: wikiTrustLevelSchema,
      /** The level lets the viewer decide on this set (its creator and role only). */
      mayDecide: z.boolean(),
      /**
       * At `peer`, the visible items the viewer last edited: theirs to leave to someone else
       * (user decision 2026-10-30). Absent from an older API.
       */
      editedByYou: z.array(z.uuid()).optional(),
      /**
       * Who decides, for the "who decides" and "why you see it" lines (wiki-collaboration-ux
       * D4): the level, or `own_edits` when the viewer's own live edits stop them at `peer`,
       * or `waiting` when a thread waits on them. Absent from an N-1 API.
       */
      reason: wikiTrustReasonSchema.optional(),
    })
    .optional(),
  /** Open threads on the set and its visible items, without text (D16); absent from N-1. */
  openThreads: z.array(wikiOpenThreadSchema).optional(),
  /**
   * Resolve with the agent (wiki-resolve-with-agent D1), on one set's read for its creator only:
   * absent means no trigger (another viewer, a set made outside a conversation, an N-1 API).
   */
  resolve: wikiResolveEligibilitySchema.optional(),
  /** The set's latest drafted or sent agent revision, counts only (D7); absent: none or N-1. */
  agentRevision: wikiAgentRevisionSchema.optional(),
  reviewComment: z.string().nullable(),
  reviewCommentByName: z.string().nullable().optional(),
  reviewCommentBy: userIdSchema.nullable(),
  reviewCommentAt: z.string().nullable(),
  provenance: z.object({
    createdByName: z.string().nullable(),
    conversation: z.object({ id: z.uuid(), title: z.string().nullable() }).nullable(),
    conversationDeleted: z.boolean(),
    mcp: z.object({ clientHost: z.string().nullable(), summary: z.string() }).nullable(),
    revertsChangeSetId: z.uuid().nullable(),
    revertsDocumentId: documentIdSchema.nullable(),
    revertsToVersion: version.nullable(),
  }),
  items: z.array(wikiChangeItemSchema),
  hiddenItems: z.boolean(),
});
export type WikiChangeSet = z.infer<typeof wikiChangeSetSchema>;

/** Why the viewer meets a change set's decision as they do (wiki-collaboration-ux D4). */
export type WikiTrustReason = z.infer<typeof wikiTrustReasonSchema>;

/** A change item located from a legacy draft link (a draft ID is its item's ID). */
export const wikiChangeItemRefSchema = z.object({ changeSetId: z.uuid(), itemId: z.uuid() });
export type WikiChangeItemRef = z.infer<typeof wikiChangeItemRefSchema>;
export const wikiChangeSetPublishPreviewSchema = z.object({
  items: z.array(z.object({ id: z.uuid(), preview: publishPreviewSchema.nullable() })),
});
export const wikiActivityEntrySchema = wikiChangeSetSchema
  .omit({
    items: true,
    rationale: true,
    reviewComment: true,
  })
  .extend({
    items: z.array(
      wikiChangeItemSchema.pick({
        id: true,
        op: true,
        title: true,
        pageId: true,
        publishedDocumentId: true,
        publishedVersion: true,
        status: true,
      }),
    ),
    approvers: z.array(z.object({ id: userIdSchema, name: z.string().nullable() })),
  });
export type WikiActivityEntry = z.infer<typeof wikiActivityEntrySchema>;

/**
 * One row of the Changes inbox's Closed view (wiki-collaboration-ux D5): a published or
 * discarded set the viewer can see. The title stays when any item is readable; the items are the
 * readable ones only, `hiddenCount` the rest. Never proposal text, sources or rationale.
 */
export const wikiClosedChangeSetSchema = z.object({
  id: z.uuid(),
  title: z.string(),
  status: z.enum(["published", "discarded"]),
  origin: wikiChangeOriginSchema,
  createdBy: userIdSchema,
  createdByName: z.string().nullable(),
  /** Who published or discarded it, and when; null for a sweep of a forgotten working set. */
  decidedBy: userIdSchema.nullable(),
  decidedByName: z.string().nullable(),
  decidedAt: z.string(),
  /** The discard's comment; null when none was given or an item is hidden from the viewer. */
  comment: z.string().nullable(),
  items: z.array(
    wikiChangeItemSchema.pick({
      id: true,
      op: true,
      title: true,
      status: true,
      publishedDocumentId: true,
      publishedVersion: true,
    }),
  ),
  /** Items of the set the viewer cannot see ("+1 change you can't see"). */
  hiddenCount: z.int().min(0),
  /**
   * Whether the set's read-only review opens for the viewer (an item's own label is readable).
   * False for a page read only through a declassified published version: open that version.
   */
  openable: z.boolean().optional(),
});
export type WikiClosedChangeSet = z.infer<typeof wikiClosedChangeSetSchema>;
export const wikiClosedChangeSetsSchema = z.object({
  items: z.array(wikiClosedChangeSetSchema),
  /** The cursor of the next page; null on the last. */
  next: z.string().nullable(),
});
export type WikiClosedChangeSets = z.infer<typeof wikiClosedChangeSetsSchema>;
/** The Closed view's query: an opaque cursor and a title search. */
export const wikiClosedChangeSetsQuerySchema = z.object({
  cursor: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{6}Z~[0-9a-f-]{36}$/i)
    .optional(),
  q: z.string().trim().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(50).optional(),
});
/** A discard's optional comment (2 000 characters at most), shown in Closed. */
export const discardWikiChangeSetRequestSchema = z.object({
  comment: z.string().trim().min(1).max(2000).optional(),
});

export const wikiReviewQueueSchema = z.object({
  items: z.array(wikiReviewItemSchema),
  changeSets: z.array(wikiChangeSetSchema),
  multiItemChangeSets: z.int().min(0),
});
export type WikiReviewQueue = z.infer<typeof wikiReviewQueueSchema>;

// ── Pending changes touching a page (wiki-collaboration-ux D9) ─────────────────────────────

/** A submitted open set touching a page, as the viewer may see it: only its visible items. */
export const wikiPendingSetSchema = z.object({
  id: z.uuid(),
  /** Null when the viewer may not see every item of the set (its title may name them). */
  title: z.string().nullable(),
  status: wikiChangeSetStatusSchema,
  items: z.array(
    z.object({
      id: z.uuid(),
      op: wikiChangeOpSchema,
      /** The heading slugs an edit changes, in page order; empty for the other kinds. */
      sections: z.array(z.string()),
    }),
  ),
});
export type WikiPendingSet = z.infer<typeof wikiPendingSetSchema>;
export const wikiPendingListSchema = z.object({ sets: z.array(wikiPendingSetSchema) });
export type WikiPendingList = z.infer<typeof wikiPendingListSchema>;
